import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';

type ItemGroup = { id: string; name: string; source_system: string; item_count: number };

export function ItemGroupsPage() {
  const { user } = useAuth();
  const writable = can(user, 'item-groups', 'create');
  const [rows, setRows] = useState<ItemGroup[]>([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows((await api.list<ItemGroup>('/api/item-groups/manage')));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '读取物料组失败');
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const create = async () => {
    const clean = name.trim();
    if (!clean) { setError('请输入物料组名称'); return; }
    setBusy(true); setError(null);
    try {
      await api.post('/api/item-groups', { name: clean });
      setName('');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '新增物料组失败');
    } finally { setBusy(false); }
  };

  const remove = async (group: ItemGroup) => {
    if (!window.confirm(`确定删除物料组“${group.name}”吗？删除仅适用于本系统新增且没有关联物料、子组和启用折扣规则的组。`)) return;
    setBusy(true); setError(null);
    try {
      await api.del(`/api/item-groups/${encodeURIComponent(group.id)}`);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '删除物料组失败');
    } finally { setBusy(false); }
  };

  return <section className="panel">
    <div className="panel-head"><div><h2>物料组</h2><div className="desc">ERPNext 业务物料组承袭与本系统新增分组；折扣规则和物料档案共用此列表。</div></div><div className="spacer"/><button className="btn" onClick={() => void load()}>刷新</button></div>
    <div className="panel-body">
      {error ? <div className="notice bad" role="alert">{error}</div> : null}
      {writable ? <div style={{ display: 'flex', gap: 8, maxWidth: 620, marginBottom: 16 }}>
        <input className="txt" value={name} maxLength={120} placeholder="新增业务物料组名称" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void create(); }} />
        <button className="btn primary" disabled={busy || !name.trim()} onClick={() => void create()}>新增物料组</button>
      </div> : null}
      <div className="table-wrap"><table className="grid" style={{ minWidth: 540 }}>
        <thead><tr><th>物料组</th><th>来源</th><th className="num">关联物料</th>{writable ? <th>操作</th> : null}</tr></thead>
        <tbody>{rows.map((group) => <tr key={group.id}>
          <td><strong>{group.name}</strong></td>
          <td>{group.source_system === 'integral-local' ? '本系统新增' : 'ERPNext 承袭'}</td>
          <td className="num">{group.item_count}</td>
          {writable ? <td>{group.source_system === 'integral-local'
            ? <button className="btn danger" disabled={busy || group.item_count > 0} title={group.item_count > 0 ? '该组仍有关联物料，需先调整物料归属' : undefined} onClick={() => void remove(group)}>删除</button>
            : <button className="btn" disabled title="ERPNext 承袭物料组保留来源关系；如需清理，请先调整物料归属并确认不再使用。">删除</button>}</td> : null}
        </tr>)}</tbody>
      </table></div>
      {loading ? <div className="muted" style={{ padding: 12 }}>加载中…</div> : null}
      {!loading && rows.length === 0 ? <div className="muted" style={{ padding: 12 }}>暂无物料组</div> : null}
      <p className="muted" style={{ marginBottom: 0 }}>只展示截图中的维修工艺组、非标件和制造件。新增分组归入“制造件”；更深层的尺寸/部件组仍保留在 ERPNext 关系树内，用于物料归属及折扣向上匹配。</p>
    </div>
  </section>;
}
